import { useLocation, Link } from 'react-router-dom';
import { useRealtimeStore } from '@repletoryreach/sdk-utils/store';
import { Group, ActionIcon, Badge, Box, Stack, Text, ThemeToggle } from '@repletoryreach/sdk-ui';
import { BreadcrumbTrail, EnvironmentBadge } from '@repletoryreach/sdk-ui/data-display';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { Burger, useAppShell } from '@repletoryreach/sdk-ui/nav';
import { NotificationsBell } from './NotificationsBell';
import { ImpersonationPill } from './ImpersonationPill';
import { ProfileMenu } from '../components/ProfileMenu';
import { SpotlightLauncher } from '../components/SpotlightLauncher';
import { LocalePicker } from '../../i18n/LocalePicker';
import { CATALOGS } from '../../i18n/catalog';
import { useT, type Translate } from '../../i18n/I18nProvider';
import { resolveBreadcrumbs } from '../breadcrumbs';
import { DOCS_URL } from '../external-links';
import { BrandLogo } from '../../components/BrandLogo';
import { BrandWordmark } from '../../components/BrandWordmark';

/**
 * The picker is worth a slot in the header only when there is more than one
 * language to pick. Four of the five catalogues are empty by design, so today
 * this hides it: choosing isiZulu changed nothing but the number formatting,
 * which read as a broken translation rather than a parked one.
 */
const TRANSLATED_LOCALES = Object.values(CATALOGS).filter((c) => Object.keys(c).length > 0).length;

function envName(): 'production' | 'staging' | 'development' | 'local' {
  const mode = import.meta.env.MODE;
  if (mode === 'production') return 'production';
  if (mode === 'staging') return 'staging';
  if (typeof window !== 'undefined' && /localhost|127\.0\.0\.1/.test(window.location.hostname)) return 'local';
  return 'development';
}

export interface HeaderBarProps {
  /** ≤768px layout: the burger opens the off-canvas nav instead of toggling navbar mode. */
  mobile?: boolean;
  /** Off-canvas nav state (mobile only); drives the burger's X state. */
  menuOpened?: boolean;
  /** Opens the off-canvas nav (mobile only). */
  onMenuClick?: () => void;
}

export function HeaderBar({ mobile = false, menuOpened = false, onMenuClick }: HeaderBarProps) {
  const t = useT();
  const location = useLocation();
  const { navbarMode, setNavbarMode } = useAppShell();

  const navbarOpen = navbarMode === 'full';
  const toggleNavbar = () => setNavbarMode(navbarOpen ? 'icon' : 'full');
  const crumbs = resolveBreadcrumbs(location.pathname, t);
  const realtime = useRealtimeStore((s) => s.status);
  const brandVariant = mobile ? 'mobile' : navbarOpen ? 'full' : 'icon';

  return (
    <div className="admin-shell__header-row">
      {/* ── Top-left: brand ─ width is locked to --admin-navbar-width via
       *    .admin-shell__brand so the divider lines up with the sidebar.
       *    On phones the zone shrinks to the avatar (no sidebar to align to). */}
      <div className={`admin-shell__brand admin-shell__brand--${brandVariant}`}>
        <BrandLogo size={36} style={{ color: 'var(--rr-color-primary)', flexShrink: 0 }} />
        {navbarOpen && !mobile && (
          <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
            <Group gap="xs" align="center" wrap="nowrap">
              <Text fw={800} role="subtitle"><BrandWordmark /></Text>
              <Badge color="orange" size="xs" variant="light">{t('shell.brand.badge')}</Badge>
            </Group>
            <Text role="caption" c="var(--rr-color-text-muted)">{t('shell.brand.subtitle')}</Text>
          </Stack>
        )}
      </div>

      {/* ── Middle: burger + breadcrumbs + env ───────────────────────── */}
      <div className="admin-shell__header-mid">
        {mobile ? (
          <Burger
            size="sm"
            opened={menuOpened}
            onClick={onMenuClick}
            label={menuOpened ? t('shell.nav.close') : t('shell.nav.open')}
          />
        ) : (
          <Tooltip label={navbarOpen ? t('shell.nav.collapse') : t('shell.nav.expand')} position="bottom">
            <Burger size="sm" opened={navbarOpen} onClick={toggleNavbar} label={t('shell.nav.toggle')} />
          </Tooltip>
        )}
        <BreadcrumbTrail
          items={mobile ? crumbs.slice(-1) : crumbs}
          separator={<span style={{ color: 'var(--rr-color-text-muted)' }}>›</span>}
          renderLink={(href, label) => (
            <Link to={href} style={{ color: 'var(--rr-color-text-muted)', textDecoration: 'none' }}>{label}</Link>
          )}
        />
        <EnvironmentBadge env={envName()} />
        {!mobile && <ImpersonationPill />}
      </div>

      {/* ── Top-right: search + actions + profile ───────────────────── */}
      <div className="admin-shell__header-right">
        {!mobile && (
          <>
            <SpotlightLauncher />
            <Box style={{ width: 1, height: 24, background: 'var(--rr-color-border)' }} aria-hidden />
            <RealtimeDot status={realtime} t={t} />
            <ThemeToggle iconOnly size="xs" />
            {TRANSLATED_LOCALES > 1 && <LocalePicker />}
          </>
        )}
        {mobile && <ThemeToggle iconOnly size="xs" />}
        <NotificationsBell />
        {!mobile && DOCS_URL !== '' && (
          <>
            <Tooltip label={t('shell.help.tooltip')} position="bottom">
              <ActionIcon
                variant="subtle"
                aria-label={t('shell.help.aria')}
                onClick={() => window.open(DOCS_URL, '_blank', 'noopener')}
              >
                <HelpIcon />
              </ActionIcon>
            </Tooltip>
            <Box style={{ width: 1, height: 24, background: 'var(--rr-color-border)' }} aria-hidden />
          </>
        )}
        <ProfileMenu />
      </div>
    </div>
  );
}

/**
 * Connection state for the realtime hub. The store already tracked it and
 * nothing rendered it, so a console that had quietly stopped receiving events
 * looked exactly like a quiet platform.
 */
function RealtimeDot({ status, t }: { status: string; t: Translate }) {
  const connected = status === 'connected';
  const busy = status === 'connecting' || status === 'reconnecting';
  const label = connected
    ? t('shell.realtime.live')
    : busy
      ? t('shell.realtime.connecting')
      : t('shell.realtime.offline');
  const colour = connected
    ? 'var(--rr-color-success)'
    : busy
      ? 'var(--rr-color-warning)'
      : 'var(--rr-color-text-subtle)';

  // A bare dot said the state only to someone who already knew what the dot
  // meant. The pill carries the word as well, which is what the design shows
  // and what makes a silent disconnection legible.
  return (
    <Tooltip label={`${t('shell.realtime.aria')}: ${label}`} position="bottom">
      <span
        role="status"
        aria-label={`${t('shell.realtime.aria')}: ${label}`}
        className="admin-shell__conn"
        style={{ ['--admin-conn' as string]: colour }}
      >
        <span className="admin-shell__conn-dot" aria-hidden />
        <span className="admin-shell__conn-label">{label}</span>
      </span>
    </Tooltip>
  );
}

function HelpIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="8" cy="8" r="6" />
      <path d="M6 6.5a2 2 0 014 0c0 1.5-2 1.5-2 3" />
      <circle cx="8" cy="12" r="0.5" fill="currentColor" />
    </svg>
  );
}
